import { onMounted, ref, watch } from 'vue';
import { useI18n, usePlanLimits, type PlanLimitsProp } from '@/composables';

type FlowConditionEntry = {
  predicate: string;
  values: string;
};

type FlowConditions = Record<string, FlowConditionEntry>;

type FlowSingleValueConditionOptions = {
  conditionKey: string;
  defaultValue: string;
  planLimitsKey?: PlanLimitsProp;
};

export const useFlowSingleValueCondition = (
  conditions: FlowConditions,
  options: FlowSingleValueConditionOptions,
) => {
  const { t } = useI18n();
  const planLimitsControl = options.planLimitsKey ? usePlanLimits(options.planLimitsKey) : null;

  const value = ref(options.defaultValue);
  const predicate = ref('EQUALS');

  const hasPlanLimitsRestriction = (limitKey: PlanLimitsProp) =>
    planLimitsControl?.hasPlanLimitsRestriction(limitKey) ?? false;

  watch(
    () => conditions,
    () => {
      if (!conditions[options.conditionKey]) {
        conditions[options.conditionKey] = {
          predicate: 'EQUALS',
          values: options.defaultValue,
        };
      }
    },
    { immediate: true },
  );

  watch(value, (newValue) => {
    conditions[options.conditionKey].values = newValue;
  });

  watch(predicate, (newValue) => {
    conditions[options.conditionKey].predicate = newValue;
  });

  onMounted(() => {
    if (conditions[options.conditionKey]) {
      value.value = conditions[options.conditionKey].values;
      predicate.value = conditions[options.conditionKey].predicate;
    }
  });

  return {
    t,
    value,
    predicate,
    hasPlanLimitsRestriction,
  };
};
